import { useRef, useState } from "react";
import HtmlMarkdownEditor from "../Utils/HtmlEditor/HtmlMarkdownEditor";
import HtmlRenderer from "../Utils/HtmlEditor/HtmlRenderer";

/**
 * MarkdownEditor Component
 *
 * Provides an admin-style rich text editor (HtmlMarkdownEditor)
 * with a preview renderer (HtmlRenderer).
 *
 * Features:
 * - Rich text editing with fonts, formatting, images, videos, and hyperlinks.
 * - Preview button to render sanitized HTML.
 * - Custom fonts integrated with Quill.
 * - Ref forwarding to directly access the underlying Quill instance.
 * - Supports default initial value for the editor.
 *
 * Usage Example:
 * const editorRef = useRef(null);
 *
 * <MarkdownEditor />
 *
 * // Access Quill instance:
 * console.log(editorRef.current.getText());
 */

export default function MarkdownEditor() {
    const editorRef = useRef(null);
    const [htmlOutput, setHtmlOutput] = useState("");

    // Initial rich demo content as Quill Delta
    const initialValue = {
        ops: [
            { insert: "Welcome to Enhanced Quill Editor!" },
            { insert: "\n", attributes: { header: 1 } },
            { insert: "🎨 Font Features: Try different fonts like " },
            { insert: "Arial", attributes: { font: "arial" } },
            { insert: " • " },
            { insert: "Georgia", attributes: { font: "georgia", bold: true } },
            { insert: " • " },
            { insert: "Courier New", attributes: { font: "courier-new", italic: true } },
            { insert: "\n" },
            { insert: "📝 Rich Formatting: Use the toolbar for " },
            { insert: "colors", attributes: { color: "#e60000" } },
            { insert: ", " },
            { insert: "backgrounds", attributes: { background: "#ffff00" } },
            { insert: ", headers, lists, alignment and more!" },
            { insert: "\n" },
            { insert: "🖼️ Image Resizing: Add images and drag the corners to resize them!" },
            { insert: "\n" },
            { insert: "📹 Media Support: Links, videos, and more using the toolbar!" },
            { insert: "\n" },
            { insert: { image: "https://picsum.photos/400/300" } },
            { insert: "\n" },
        ],
    };

    return (
        <div style={{ padding: "20px" }}>
            <h1>Markdown Editor</h1>

            {/* Editor */}
            <HtmlMarkdownEditor
                ref={editorRef}
                defaultValue={initialValue}
                onPreview={(html) => setHtmlOutput(html)}
            />

            {/* Renderer */}
            {htmlOutput && (
                <div style={{ marginTop: "30px" }}>
                    <h2>Renderer Output:</h2>
                    <HtmlRenderer html={htmlOutput} />
                </div>
            )}
        </div>
    );
}
